import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Mail, Building2, Calendar, Plus, Trash2, Save, MapPin } from "lucide-react";
import { ClientStatusBadge } from "@/shared/components/ClientStatusBadge";
import { ClientStatusSelect } from "@/shared/components/ClientStatusSelect";
import { ClientStatusType } from "@/shared/types/clientStatus";
import { ADMIN_ROUTES } from "@/shared/constants/paths";
import CircularProgress from "@/shared/components/CircularProgress";
import { useState } from "react";
import { AssignEstateDialog } from "./components/AssignEstateDialog";
import AdminLayout from "@/pages/layouts/AdminLayout";

// Type for user data from user_estates join query
interface UserPersonUser {
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

const LawyerDetail = () => {
  const { lawyerId } = useParams<{ lawyerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [barNumber, setBarNumber] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch lawyer details
  const {
    data: lawyer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-lawyer-detail", lawyerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lawyers")
        .select(
          `
          *,
          user:users(user_id, email, first_name, last_name, full_name, phone_number, created_at)
        `,
        )
        .eq("id", lawyerId)
        .single();

      if (error) throw error;

      // Set form values
      setFirmName(data.firm_name || "");
      setBarNumber(data.bar_number || "");

      return data;
    },
    enabled: !!lawyerId,
  });

  // Fetch lawyer's assignments
  const { data: assignments, refetch: refetchAssignments } = useQuery({
    queryKey: ["admin-lawyer-assignments", lawyer?.user_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lawyer_estates")
        .select(
          `
          *,
          person:estates(estate_id, full_name, date_of_death, location, lawyer_client_status)
        `,
        )
        .eq("lawyer_user_id", lawyer?.user_id)
        .order("assigned_at", { ascending: false });

      if (error) throw error;

      // Fetch account holders for each person
      const estateIds = (data || [])
        .map((d) => d.person?.estate_id)
        .filter((id): id is number => id != null);

      const accountHolderMap: Record<number, { name: string; email: string }> = {};

      if (estateIds.length > 0) {
        const { data: userEstates } = await supabase
          .from("user_estates")
          .select("estate_id, user:users(full_name, first_name, last_name, email)")
          .in("estate_id", estateIds);

        // Create a map of estate_id to account holder
        userEstates?.forEach((up) => {
          const userArray = up.user as UserPersonUser[] | null;
          const user = userArray?.[0];
          if (up.estate_id && user) {
            accountHolderMap[up.estate_id] = {
              name:
                user.full_name ||
                `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                "Unknown",
              email: user.email || "",
            };
          }
        });
      }

      return (data || []).map((assignment) => ({
        ...assignment,
        accountHolder: assignment.person?.estate_id
          ? accountHolderMap[assignment.person.estate_id] || null
          : null,
      }));
    },
    enabled: !!lawyer?.user_id,
  });

  // Update lawyer mutation
  const updateLawyerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("lawyers")
        .update({
          firm_name: firmName || null,
          bar_number: barNumber || null,
        })
        .eq("id", lawyerId);

      if (error) throw error;
    },
    onSuccess: () => {
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ["admin-lawyer-detail", lawyerId] });
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase.from("lawyer_estates").delete().eq("id", assignmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      refetchAssignments();
    },
  });

  // Update client status mutation
  const updateClientStatusMutation = useMutation({
    mutationFn: async ({ estateId, status }: { estateId: number; status: ClientStatusType }) => {
      const { error } = await supabase
        .from("estates")
        .update({ lawyer_client_status: status })
        .eq("estate_id", estateId);

      if (error) throw error;
    },
    onSuccess: () => {
      refetchAssignments();
    },
  });

  const handleFieldChange = (field: "firmName" | "barNumber", value: string) => {
    if (field === "firmName") setFirmName(value);
    else setBarNumber(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateLawyerMutation.mutate();
  };

  const handleRemoveAssignment = (assignmentId: string) => {
    if (confirm("Are you sure you want to remove this estate assignment?")) {
      removeAssignmentMutation.mutate(assignmentId);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Lawyers" titleLink={ADMIN_ROUTES.LAWYERS} backgroundColor="stone-100">
        <div className="flex items-center justify-center py-12">
          <CircularProgress />
        </div>
      </AdminLayout>
    );
  }

  if (error || !lawyer) {
    return (
      <AdminLayout title="Lawyers" titleLink={ADMIN_ROUTES.LAWYERS} backgroundColor="stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-stone-600">Lawyer not found.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Lawyers" titleLink={ADMIN_ROUTES.LAWYERS} backgroundColor="stone-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div>
            <h1 className="text-2xl text-stone-900 mb-2">
              {lawyer.user?.full_name ||
                `${lawyer.user?.first_name || ""} ${lawyer.user?.last_name || ""}`.trim() ||
                "Unknown Lawyer"}
            </h1>
            <a
              href={`mailto:${lawyer.user?.email}`}
              className="text-sm text-teal-600 flex items-center gap-1.5 no-underline hover:underline"
            >
              <Mail className="h-4 w-4 text-teal-600" />
              {lawyer.user?.email}
            </a>
          </div>
        </div>

        {/* Lawyer Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">Lawyer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firmName">Firm Name</Label>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-stone-400" />
                  <Input
                    id="firmName"
                    value={firmName}
                    onChange={(e) => handleFieldChange("firmName", e.target.value)}
                    placeholder="Enter firm name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="barNumber">Bar Number</Label>
                <Input
                  id="barNumber"
                  value={barNumber}
                  onChange={(e) => handleFieldChange("barNumber", e.target.value)}
                  placeholder="Enter bar number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t text-sm">
              <div>
                <p className="text-stone-500">Email</p>
                <p className="font-medium text-stone-900 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-stone-400" />
                  {lawyer.user?.email}
                </p>
              </div>
              <div>
                <p className="text-stone-500">Added as Lawyer</p>
                <p className="font-medium text-stone-900 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-stone-400" />
                  {new Date(lawyer.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {hasChanges && (
              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} disabled={updateLawyerMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  {updateLawyerMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estates Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Estates</CardTitle>
              <Button onClick={() => setIsAssignDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Assign Estate
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!assignments || assignments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-stone-500 mb-4">No estates assigned to this lawyer yet.</p>
                <Button variant="outline" onClick={() => setIsAssignDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Assign First Estate
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="py-4 flex items-center justify-between -mx-6 px-6 cursor-pointer hover:bg-stone-50 transition-colors"
                    onClick={() => navigate(`/admin/decedent/${assignment.person?.estate_id}`)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-stone-900">
                          {assignment.person?.full_name || "Unknown"} Estate
                        </h3>
                        <ClientStatusBadge
                          status={assignment.person?.lawyer_client_status as ClientStatusType}
                        />
                      </div>
                      {(assignment.accountHolder || assignment.person?.location) && (
                        <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                          {assignment.accountHolder && <span>{assignment.accountHolder.name}</span>}
                          {assignment.person?.location && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="h-4 w-4" />
                              {assignment.person.location}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div onClick={(e) => e.stopPropagation()}>
                        <ClientStatusSelect
                          value={assignment.person?.lawyer_client_status as ClientStatusType}
                          onValueChange={(status) => {
                            if (assignment.person?.estate_id) {
                              updateClientStatusMutation.mutate({
                                estateId: assignment.person.estate_id,
                                status,
                              });
                            }
                          }}
                          disabled={updateClientStatusMutation.isPending}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAssignment(assignment.id);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Assign Estate Dialog */}
      <AssignEstateDialog
        open={isAssignDialogOpen}
        onOpenChange={setIsAssignDialogOpen}
        lawyerUserId={lawyer.user_id}
        onSuccess={() => {
          refetchAssignments();
          setIsAssignDialogOpen(false);
        }}
      />
    </AdminLayout>
  );
};

export default LawyerDetail;
